import { Fragment, useState } from 'react';
import { useAsync } from 'react-async';
import { useHistory } from 'react-router';
import {
  getBillingRateById,
  deleteBillingRate,
  updateBillingRate,
  approveBillingRateById,
} from '../../../api/services/billingRateService';
import { ResponseStatus } from '../../../components/notifications';
import Layout from '../../../components/layout';
import SubmitButton from '../../../components/submitButton';
import { useParams } from 'react-router-dom';
import LayoutError from '../../../components/layoutError';
import { getDisciplines } from '../../../api/services/disciplineService';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';
import { getDistrict } from '../../../api/services/districtService';
import Program from '../../../api/models/program';
import { useAppSelector } from '../../../app/hooks';
import RejectWithFeedbackRequest from '../../../api/requests/rejectWithFeedbackRequest';
import Select from 'react-select';
import { graySelectStyles } from '../../../common/selectStyles';

type Params = {
  districtId: string;
  id: string;
};

const fetchBillingRate = async ({ id }: any) => {
  return await getBillingRateById(id);
};

const fetchDistrict = async ({ districtId }: any) => {
  return await getDistrict(districtId);
};

const approveBillingRate = async (args: any[]) => {
  return await approveBillingRateById(args[0]);
}

const EditBillingRates = () => {
  const history = useHistory();
  const currentUser = useAppSelector(s => s.user);
  const { id } = useParams<Params>();
  const { districtId } = useParams<Params>();
  const [rate, setRate] = useState<number>(0);
  const [disciplineId, setDisciplineId] = useState<number>(0);
  const [programId, setProgramId] = useState<number>(-1);
  const [programs, setPrograms] = useState<Program[]>();
  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();
  const [isDirector, setIsDirector] = useState<boolean>(currentUser && currentUser.roles === "Director");
  const [isApproved, setIsApproved] = useState<boolean>(false);

  const disciplines = useAsync({
    promiseFn: getDisciplines,
  });

  const fetchData = useAsync({
    promiseFn: fetchBillingRate,
    id,
    onResolve: (r) => {
      const billingRate: any = Object.entries(r.data)[0][1];
      setRate(billingRate.rate);
      setStartDate(moment(billingRate.startDate).toDate());
      setEndDate(moment(billingRate.endDate).toDate());
      setDisciplineId(billingRate.disciplineId);
      setProgramId(billingRate.programId ?? -1);
      setIsApproved(billingRate.approvedDate !== null);
    }
  });

  useAsync({
    promiseFn: fetchDistrict,
    districtId,
    onResolve: (r) => {
      setPrograms(r.data.district.programs);
    },
  });

  const approve = useAsync({
    deferFn: approveBillingRate,
    onResolve: (r) => {
      if (r.data) history.push(`/admin/districts/${districtId}`);
    },
  });

  const saveBillingRate = async () => {
    if (!id) {
      return Promise.reject('Missing id.');
    }

    return await updateBillingRate(Number(id), {
      billingRateId: Number(id),
      districtId: Number(districtId),
      disciplineId: Number(disciplineId),
      programId: (programId !== -1) ? programId : undefined,
      startDate: startDate!.toLocaleDateString(),
      endDate: endDate!.toLocaleDateString(),
      isVisible: true,
      rate: rate
    });
  };

  const save = useAsync({
    deferFn: saveBillingRate,
    onResolve: () => {
      history.push(`/admin/districts/${districtId}`, {
        message: `Billing rate saved successfully!`,
      });
    },
  });

  const removeBillingRate = async () => {
    if (!id) {
      return Promise.reject('Missing id.');
    }

    return await deleteBillingRate(Number(id), {
      feedback: "",
    } as RejectWithFeedbackRequest);
  };

  const remove = useAsync({
    deferFn: removeBillingRate,
    onResolve: () => {
      history.push(`/admin/districts/${districtId}`, {
        message: `Billing rate removed successfully!`,
      });
    },
  });

  if (fetchData.error) return <LayoutError />;

  const handleSubmit = () => {
    save.run();
  };

  const handleRemove = () => {
    remove.run();
  };

  const handleApprove = () => {
    if (!id) {
      return Promise.reject('Missing id.');
    }

    approve.run(id);
  }

  return (
    <Layout isLoading={fetchData.isPending}>
      <Fragment>
        <ResponseStatus state={save} />
        <ResponseStatus state={remove} />
        <div>
          <div className='grid grid-cols-6 gap-6 mb-6'>
            <div className='col-span-6 sm:col-span-6'>
              <label className='mb-2'>Discipline:<span className='pl-1 text-red-500'>*</span></label>
              <Select
                className='w-full'
                value={disciplines?.data?.data.disciplines.sort((a, b) => a.name.localeCompare(b.name)).map(d => ({ value: d.disciplineId, label: d.name })).find(o => o.value === disciplineId) || null}
                onChange={(selected) => setDisciplineId(Number(selected?.value ?? 0))}
                options={disciplines?.data?.data.disciplines.sort((a, b) => a.name.localeCompare(b.name)).map(d => ({ value: d.disciplineId, label: d.name })) || []}
                styles={graySelectStyles}
              />
            </div>
            <div className='col-span-6 sm:col-span-6'>
              <label className='mb-2'>Program:</label>
              <Select
                className='w-full'
                value={[
                  { value: -1, label: 'Select Program' },
                  ...(programs?.sort((a, b) => a.name.localeCompare(b.name)).map(d => ({ value: d.programId, label: d.name })) || [])
                ].find(o => o.value === programId) || null}
                onChange={(selected) => setProgramId(Number(selected?.value ?? -1))}
                options={[
                  { value: -1, label: 'Select Program' },
                  ...(programs?.sort((a, b) => a.name.localeCompare(b.name)).map(d => ({ value: d.programId, label: d.name })) || [])
                ]}
                styles={graySelectStyles}
              />
            </div>
            <div className='col-span-6 sm:col-span-6 rate-field'>
              <div className='text-sm'>
                <label htmlFor='rate' className='primary'>
                  Rate<span className='pl-1 text-red-500'>*</span>
                </label>
              </div>
              <input
                type="number"
                name='rate'
                min='0'
                className='w-full'
                onChange={(e) => setRate(Number(e.target.value))}
                value={rate.toString()}
              />
            </div>
            <div className='col-span-6 sm:col-span-6'>
              <label className='mb-2'>Start Date:<span className='pl-1 text-red-500'>*</span></label>
              <DatePicker
                id="startDatePicker"
                name='rate'
                selected={startDate}
                onChange={(date: Date) => setStartDate(date!)}
                dateFormat="MM/dd/yyyy"
              />
            </div>
            <div className='col-span-6 sm:col-span-6'>
              <label className='mb-2'>End Date:<span className='pl-1 text-red-500'>*</span></label>
              <DatePicker
                id="endDatePicker"
                name='rate'
                selected={endDate}
                minDate={moment(startDate).toDate()}
                onChange={(date: Date) => setEndDate(date!)}
                dateFormat="MM/dd/yyyy"
              />
            </div>
          </div>
          <div className='flex justify-between'>
            {/* Only show remove button if this is an active or scheduled rate */}
            {endDate !== undefined && (
              new Date(moment(endDate).toDate()).setHours(0, 0, 0, 0) >= new Date().setHours(0, 0, 0, 0) ?
                <SubmitButton
                  text='Remove Billing Rate'
                  spinning={save.isPending}
                  disabled={rate === 0}
                  onClick={handleRemove}
                  className="secondary"
                />
                :
                <></>
            )}

            {/*isDirector &&
              <SubmitButton
                text='Approve Billing Rate'
                spinning={approve.isPending}
                disabled={isApproved}
                onClick={handleApprove}
              />
            */}

            <SubmitButton
              text='Save Billing Rate'
              spinning={save.isPending}
              disabled={rate < 0 || disciplineId === 0 || disciplineId === -1 || startDate === undefined || endDate === undefined || startDate === null || endDate === null}
              onClick={() => {
                if (rate <= 0) {
                  alert("Rate must be greater than 0 to proceed.");
                  return;
                }
                handleSubmit();
              }}
            />
          </div>
        </div>
      </Fragment>
    </Layout>
  );
};

export default EditBillingRates;